import { useState, useEffect, useLayoutEffect, useMemo, useRef, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { CautaAdaugaPartener } from '../../components/parteneri/CautaAdaugaPartener'
import { downloadFile } from '../../lib/downloadFile'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { AntetFix } from '../../components/ui/AntetFix'
import { DateRangeField } from '../../components/ui/DateField'
import { ETICHETE_CIFRE } from '@b4l/shared'

interface ImportRow {
  id: number
  periodStart: string
  periodEnd: string
  fileName: string
  status: string
  rowCount: number
  unmatchedCount: number
  createdAt: string
  client: { id: number; name: string } | null
  importedBy: { id: number; name: string } | null
  /** Lunile pe care acest import le aduce efectiv în raport. */
  activeMonths: string[]
  /** Lunile pe care le are, dar sunt luate dintr-un import mai nou. */
  supersededMonths: string[]
}

interface PreviewRow {
  ean: string | null
  sku: string | null
  productName: string | null
  productName_internal: string | null
  sku_internal: string | null
  productId: number | null
  suggestion: { id: number; name: string; sku: string; score: number } | null
  suggestionTiedSkus: string[]
  month: string | null
  quantity: number
  value: number | null
  location: string | null
}
interface OverlappingImport {
  id: number
  fileName: string
  periodStart: string
  periodEnd: string
  rowCount: number
  sharedMonths: string[]
}
/** O foaie din registru, așa cum o alege operatorul. */
interface SheetInfo {
  name: string
  rows: number
  cells: number
  preview: string[]
  hasData: boolean
  /** Ascunsă în Excel — omul nu o vede când deschide fișierul. */
  hidden: boolean
}
/** Ce știe sistemul despre perioada fișierului înainte de orice extragere. */
interface MonthSource {
  certainty: 'grila' | 'nume-cu-an' | 'nume-fara-an' | 'necunoscut'
  suggestedMonth: string | null
  needsOperator: boolean
  gridLines: number
  sheets: SheetInfo[]
  suggestedSheets: string[]
}
interface PreviewResult {
  fileName: string
  rows: PreviewRow[]
  notes: string | null
  aiUnavailable: boolean
  aiError: string | null
  gridPreview: string[]
  unmatchedCount: number
  contentHash: string
  overlaps: OverlappingImport[]
}

const STATUS_LABEL: Record<string, string> = {
  primit: 'Primit',
  in_mapare: 'În mapare',
  validat: 'Validat',
  publicat: 'Publicat',
  respins: 'Respins',
  anulat: 'Anulat',
}

/** Parametrul `agent` se trimite doar de cine are voie să filtreze (admin/contabilitate). */
function agentParams(agent: string, canFilter: boolean): { agent?: string } {
  return canFilter && agent ? { agent } : {}
}

function showError(e: unknown) {
  const msg =
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? (e as Error).message
  toast.error(msg || 'Eroare')
}

export function SellOutPage() {
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('sellout:write')
  const [importOpen, setImportOpen] = useState(false)
  // Nodul din antet in care `ReportView` isi randeaza bara de filtre.
  const [slotFiltre, setSlotFiltre] = useState<HTMLDivElement | null>(null)
  const [tab, setTab] = useState<'email' | 'importuri' | 'raport' | 'locatii'>('email')
  // Filtru pe agent. Agentul de vânzări nu-l poate schimba: serverul îi impune propriul
  // portofoliu (`canFilter:false`), iar valoarea de aici e doar informativă.
  const [agent, setAgent] = useState<string>('')
  // Filtru pe client, comun taburilor. Se aplică pe listele deja încărcate —
  // recalcularea candidaților din email durează secunde, n-are rost la fiecare schimbare.
  const [clientId, setClientId] = useState<number | null>(null)

  const scopeQ = useQuery<AgentScope>({
    queryKey: ['sellout', 'agents'],
    queryFn: async () => (await api.get('/sellout/agents')).data,
  })
  const scope = scopeQ.data
  const canFilter = !!scope?.canFilter
  const agentParam = canFilter && agent ? { agent } : {}

  const clientsQ = useQuery<{ clients: { id: number; name: string }[] }>({
    queryKey: ['sellout', 'clients', agent],
    queryFn: async () => (await api.get('/sellout/clients', { params: agentParam })).data,
  })

  const q = useQuery<{ imports: ImportRow[] }>({
    queryKey: ['sellout', 'imports', agent],
    queryFn: async () => (await api.get('/sellout/imports', { params: agentParam })).data,
    enabled: tab === 'importuri',
  })
  const imports = (q.data?.imports ?? []).filter((i) => !clientId || i.client?.id === clientId)

  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-semibold">Sell-out clienți</h1>
          <div className="flex flex-wrap items-center gap-2">
            <AgentFilter
              scope={scope}
              value={agent}
              onChange={(v) => {
                setAgent(v)
                // Alt agent = alt portofoliu; clientul ales poate să nu mai fie în listă.
                setClientId(null)
              }}
            />
            <label className="flex items-center gap-1.5 text-xs text-b4l-text-muted">
              Client
              <div className="w-56">
                <SearchSelect
                  options={(clientsQ.data?.clients ?? []).map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  value={clientId}
                  onChange={setClientId}
                  // Pe raport și pe puncte de lucru clientul e OBLIGATORIU (ambele sunt per
                  // client), pe celelalte taburi e doar un filtru. Textul spune care din ele e.
                  placeholder={
                    tab === 'raport' || tab === 'locatii' ? 'alege clientul…' : 'toți clienții'
                  }
                />
              </div>
            </label>
            {/*
              Butoanele stau pe TOATE taburile, nu doar pe „Importuri salvate".
              Ascunse acolo, nu le-a gasit nimeni: tabul implicit e „Din email", iar doi
              oameni au raportat in aceeasi zi ca „nu mai vad unde se incarca" (14.09.2026).
            */}
            {canWrite && (
              <Button
                variant="secondary"
                onClick={() =>
                  downloadFile('/sellout/template', {}, 'sablon-sellout-boost4life.xlsx').catch(
                    showError,
                  )
                }
              >
                Descarcă șablon
              </Button>
            )}
            {canWrite && (
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                Import manual
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {(['email', 'importuri', 'raport', 'locatii'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded px-3 py-1.5 text-sm ${
                tab === t
                  ? 'bg-b4l-green text-white'
                  : 'bg-white border border-b4l-border text-b4l-text hover:bg-b4l-bg'
              }`}
            >
              {t === 'email'
                ? 'Din email'
                : t === 'importuri'
                  ? 'Importuri salvate'
                  : t === 'raport'
                    ? 'Raport sell-in / sell-out'
                    : 'Pe puncte de lucru'}
            </button>
          ))}
        </div>
        {/*
          Aici aterizeaza bara de filtre a raportului, prin portal din `ReportView`.

          Prima incercare a fost s-o tin in cardul raportului si sa-l lipesc sub antet cu
          `top: var(--b4l-antet)`. Nu tine: `--b4l-antet` e INALTIMEA antetului (121px), dar
          antetul incepe la 116px in pagina, nu la 0 — asa ca filtrele ajungeau tot sub el.
          In antet nu mai e nimic de calculat: e deja blocul care ramane pe ecran.
        */}
        {tab === 'raport' && <div ref={setSlotFiltre} />}
      </AntetFix>

      {scope?.unmapped && (
        <div className="rounded border border-b4l-amber-dark/40 bg-amber-50 p-3 text-sm text-b4l-amber-dark">
          Contul tău nu e legat de un agent din ExpertAccounts, deci nu are portofoliu. Cere unui
          administrator să completeze numele agentului pe utilizator.
        </div>
      )}

      {tab === 'raport' ? (
        <ReportView clientId={clientId} slotFiltre={slotFiltre} />
      ) : tab === 'locatii' ? (
        <LocationsView clientId={clientId} />
      ) : tab === 'email' ? (
        <EmailCandidatesView
          canWrite={canWrite}
          poateLegaExpeditori={!!scope?.poateLegaExpeditori}
          agent={agent}
          canFilter={canFilter}
          clientId={clientId}
        />
      ) : (
        <SellOutImportsView
          imports={imports}
          loading={q.isLoading}
          canWrite={canWrite}
          onImport={() => setImportOpen(true)}
        />
      )}
      {importOpen && (
        <ImportModal agent={agent} canFilter={canFilter} onClose={() => setImportOpen(false)} />
      )}
    </div>
  )
}

interface AgentScope {
  agents: string[]
  canFilter: boolean
  agent: string | null
  unmapped: boolean
  /** Doar admin si contabilitate leaga un expeditor de un client (vezi API-ul). */
  poateLegaExpeditori?: boolean
}

/**
 * Selectorul de agent. Adminul/contabilitatea aleg agentul; agentul de vânzări vede doar
 * eticheta cu propriul portofoliu — filtrarea reală o face serverul, nu ecranul.
 */
function AgentFilter({
  scope,
  value,
  onChange,
}: {
  scope: AgentScope | undefined
  value: string
  onChange: (v: string) => void
}) {
  if (!scope) return null
  if (!scope.canFilter) {
    return scope.agent ? (
      <span className="rounded bg-b4l-bg px-2 py-1 text-xs text-b4l-text-muted">
        Portofoliul tău: <strong className="text-b4l-text">{scope.agent}</strong>
      </span>
    ) : null
  }
  return (
    <label className="flex items-center gap-1.5 text-xs text-b4l-text-muted">
      Agent
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm text-b4l-text"
      >
        <option value="">toți agenții</option>
        {scope.agents.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </label>
  )
}

function SellOutImportsView({
  imports,
  loading,
  canWrite,
  onImport,
}: {
  imports: ImportRow[]
  loading: boolean
  canWrite: boolean
  onImport: () => void
}) {
  const qc = useQueryClient()
  const [toDelete, setToDelete] = useState<ImportRow | null>(null)

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/sellout/imports/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sellout', 'imports'] })
      qc.invalidateQueries({ queryKey: ['sellout', 'email-candidates'] })
      qc.invalidateQueries({ queryKey: ['sellout', 'report'] })
      setToDelete(null)
      toast.success('Import șters')
    },
    onError: showError,
  })

  return (
    <>
      <p className="text-sm text-b4l-text-muted">
        Rapoartele de vânzări primite de la clienți (farmacii, lanțuri). Fiecare client trimite alt
        format — încarci fișierul, iar sistemul extrage rândurile și le potrivește cu produsele
        noastre după cod de bare sau SKU.
      </p>

      <Card>
        {loading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : imports.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            {canWrite ? (
              <button type="button" onClick={onImport} className="text-b4l-blue underline">
                Import nou
              </button>
            ) : (
              'Niciun import încă.'
            )}{' '}
            pentru a încărca primul raport.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Client</th>
                  <th className="py-2 pr-3 font-medium">Perioadă</th>
                  <th className="py-2 pr-3 font-medium">Fișier</th>
                  <th className="py-2 pr-3 font-medium">Rânduri</th>
                  <th className="py-2 pr-3 font-medium">Nemapate</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Importat de</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => (
                  <tr key={imp.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3">{imp.client?.name ?? '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">
                      {fmtPeriod(imp.periodStart, imp.periodEnd)}
                    </td>
                    <td className="py-2 pr-3 max-w-[260px] text-xs">
                      <button
                        type="button"
                        title={`Descarcă ${imp.fileName}`}
                        onClick={() =>
                          downloadFile(`/sellout/imports/${imp.id}/file`, {}, imp.fileName).catch(
                            showError,
                          )
                        }
                        className="block max-w-full truncate text-left text-b4l-blue hover:underline"
                      >
                        {imp.fileName}
                      </button>
                    </td>
                    <td className="py-2 pr-3">{imp.rowCount}</td>
                    <td className="py-2 pr-3">
                      {imp.unmatchedCount > 0 ? (
                        <span className="text-b4l-amber-dark">{imp.unmatchedCount}</span>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <StatusBadge status={imp.status} />
                      {imp.supersededMonths?.length > 0 && (
                        <div
                          className="mt-0.5 text-[10px] text-b4l-text-muted"
                          title={`Lunile ${imp.supersededMonths.join(', ')} sunt luate dintr-un raport mai nou, ca să nu se adune de două ori.`}
                        >
                          {imp.activeMonths?.length === 0
                            ? 'înlocuit integral'
                            : `înlocuit pe ${imp.supersededMonths.join(', ')}`}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-b4l-text-muted">
                      {imp.importedBy?.name ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => setToDelete(imp)}
                          className="text-xs text-b4l-red hover:underline"
                        >
                          Șterge
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {toDelete && (
        <Modal
          open
          onClose={() => setToDelete(null)}
          title="Ștergi importul?"
          footer={
            <>
              <Button variant="secondary" onClick={() => setToDelete(null)}>
                Renunță
              </Button>
              <Button
                variant="danger"
                loading={del.isPending}
                onClick={() => del.mutate(toDelete.id)}
              >
                Șterge importul
              </Button>
            </>
          }
        >
          <p className="text-sm">
            Se șterg cele <strong>{toDelete.rowCount}</strong> rânduri importate din{' '}
            <strong>{toDelete.fileName}</strong> pentru <strong>{toDelete.client?.name}</strong> (
            {fmtPeriod(toDelete.periodStart, toDelete.periodEnd)}). Raportul sell-in / sell-out se
            recalculează fără ele.
          </p>
          <p className="mt-2 text-sm text-b4l-text-muted">
            Fișierul rămâne în email, deci îl poți importa din nou. Operațiunea nu se poate anula.
          </p>
        </Modal>
      )}
    </>
  )
}

// ============================================================================
// Din email: rapoartele detectate automat în inbox
// ============================================================================

interface EmailCandidate {
  messageId: number
  attachmentIdx: number
  fileName: string
  size: number | null
  subject: string
  fromAddr: string
  originalSender: string | null
  bodyPreview: string
  receivedAt: string
  guessedClientId: number | null
  guessedClientName: string | null
  guessSource: 'profil' | 'fisier' | 'expeditor' | null
  alreadyImported: boolean
  importedClientName: string | null
  importId: number | null
}

function EmailCandidatesView({
  canWrite,
  poateLegaExpeditori,
  agent,
  canFilter,
  clientId,
}: {
  canWrite: boolean
  poateLegaExpeditori: boolean
  agent: string
  canFilter: boolean
  clientId: number | null
}) {
  const [selected, setSelected] = useState<EmailCandidate | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const q = useQuery<{ since: string; candidates: EmailCandidate[] }>({
    queryKey: ['sellout', 'email-candidates', agent],
    queryFn: async () =>
      (await api.get('/sellout/email-candidates', { params: agentParams(agent, canFilter) })).data,
    // Emailurile ajung în CRM în câteva secunde (daemonul IMAP ține conexiunea deschisă),
    // dar lista din pagină rămâne cea încărcată la deschidere. O reîmprospătăm periodic
    // și la revenirea în fereastră, ca un raport tocmai primit să nu pară că lipsește.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  })
  const all = q.data?.candidates ?? []
  const candidates = clientId ? all.filter((c) => c.guessedClientId === clientId) : all
  const since = q.data?.since ? new Date(q.data.since) : null
  const updatedAt = q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-b4l-text-muted">
          Rapoartele de vânzări detectate automat în Inbox — fără descărcare manuală. Apeși
          „Importă”, sistemul citește fișierul din email, extrage rândurile și le potrivește cu
          produsele noastre.
          {since && (
            <>
              {' '}
              Perioada afișată: din <strong>{since.toLocaleDateString('ro-RO')}</strong> până azi.
            </>
          )}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {updatedAt && (
            <span className="text-xs text-b4l-text-muted">
              actualizat {updatedAt.toLocaleTimeString('ro-RO', { timeStyle: 'short' })}
            </span>
          )}
          <Button variant="secondary" onClick={() => void q.refetch()} loading={q.isFetching}>
            Sincronizează
          </Button>
        </div>
      </div>

      <Card>
        {q.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se caută în inbox...</p>
        ) : candidates.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            {clientId && all.length > 0
              ? 'Niciun raport de la acest client în perioada afișată.'
              : 'Niciun raport de vânzări detectat în perioada afișată.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Primit</th>
                  <th className="py-2 pr-3 font-medium">Client detectat</th>
                  <th className="py-2 pr-3 font-medium">Expeditor original</th>
                  <th className="py-2 pr-3 font-medium">Fișier</th>
                  <th className="py-2 pr-3 font-medium">Subiect email</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const rowKey = `${c.messageId}-${c.attachmentIdx}`
                  const open = expanded === rowKey
                  return (
                    <Fragment key={rowKey}>
                      <tr className="border-b border-b4l-border">
                        <td className="py-2 pr-3 whitespace-nowrap text-xs text-b4l-text-muted">
                          {new Date(c.receivedAt).toLocaleDateString('ro-RO')}
                        </td>
                        <td className="py-2 pr-3">
                          {c.guessedClientName ? (
                            <>
                              <div className="font-medium">{c.guessedClientName}</div>
                              <div className="text-[10px] text-b4l-text-muted">
                                {c.guessSource === 'profil'
                                  ? 'profil client'
                                  : c.guessSource === 'expeditor'
                                    ? 'după expeditor'
                                    : 'ghicit din fișier'}
                              </div>
                            </>
                          ) : (
                            <span className="text-b4l-amber-dark">necunoscut</span>
                          )}
                        </td>
                        <td
                          className="py-2 pr-3 max-w-[200px] truncate text-xs"
                          title={c.originalSender ?? c.fromAddr}
                        >
                          {c.originalSender ?? (
                            <span className="text-b4l-text-muted">{c.fromAddr}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 max-w-[220px] truncate text-xs" title={c.fileName}>
                          📎 {c.fileName}
                        </td>
                        <td
                          className="py-2 pr-3 max-w-[180px] truncate text-xs text-b4l-text-muted"
                          title={c.subject}
                        >
                          {c.subject}
                        </td>
                        <td className="py-2 pr-3">
                          {c.alreadyImported ? (
                            <span
                              className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800"
                              title={
                                c.importedClientName
                                  ? `Importat la ${c.importedClientName}`
                                  : undefined
                              }
                            >
                              importat
                              {c.importedClientName ? ` · ${c.importedClientName}` : ''}
                            </span>
                          ) : (
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700">
                              nou
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setExpanded(open ? null : rowKey)}
                            className="mr-2 text-xs text-b4l-blue underline"
                          >
                            {open ? 'ascunde' : 'vezi emailul'}
                          </button>
                          {canWrite && !c.alreadyImported && (
                            <Button variant="secondary" onClick={() => setSelected(c)}>
                              Importă
                            </Button>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-b border-b4l-border bg-b4l-bg/60">
                          <td colSpan={7} className="p-3">
                            <div className="mb-1 text-xs text-b4l-text-muted">
                              <strong>De la (inbox):</strong> {c.fromAddr}
                              {c.originalSender && (
                                <>
                                  {' · '}
                                  <strong>Expeditor original:</strong> {c.originalSender}
                                </>
                              )}
                            </div>
                            <div className="mb-1 text-xs">
                              <strong>Subiect:</strong> {c.subject}
                            </div>
                            <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded border border-b4l-border bg-white p-2 text-[11px]">
                              {c.bodyPreview || '(corpul emailului e gol)'}
                            </pre>
                            {canWrite && poateLegaExpeditori && c.guessSource !== 'profil' && (
                              <LinkSenderForm candidate={c} agent={agent} canFilter={canFilter} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <ImportModal
          fromEmail={selected}
          agent={agent}
          canFilter={canFilter}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}

/**
 * Leagă expeditorul acestui email de un client (creează/completează profilul).
 * Odată legat, rapoartele viitoare de la aceeași adresă se identifică singure —
 * fără să depindem de cum e denumit fișierul.
 */
function LinkSenderForm({
  candidate,
  agent,
  canFilter,
}: {
  candidate: EmailCandidate
  agent: string
  canFilter: boolean
}) {
  const qc = useQueryClient()
  const [clientId, setClientId] = useState<number | null>(candidate.guessedClientId)
  // Preferăm expeditorul original (clientul), nu adresa colegului care a forwardat.
  const [sender, setSender] = useState(candidate.originalSender ?? candidate.fromAddr)

  const clientsQ = useQuery<{ clients: { id: number; name: string }[] }>({
    queryKey: ['sellout', 'clients', agent],
    queryFn: async () =>
      (await api.get('/sellout/clients', { params: agentParams(agent, canFilter) })).data,
  })

  const link = useMutation({
    mutationFn: async () =>
      (await api.post('/sellout/profiles/link-sender', { clientId, sender })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sellout', 'email-candidates'] })
      qc.invalidateQueries({ queryKey: ['sellout', 'profiles'] })
      toast.success('Expeditor legat — rapoartele viitoare se vor recunoaște singure')
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message || 'Nu am putut lega expeditorul',
      ),
  })

  const domain = sender.includes('@') ? `@${sender.split('@')[1]}` : null

  return (
    <div className="mt-3 rounded border border-b4l-border bg-white p-3">
      <div className="mb-2 text-xs font-medium">Leagă expeditorul de un client</div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Expeditor</span>
          <input
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            className="w-64 rounded border border-b4l-border px-2 py-1.5 text-xs"
          />
        </label>
        <div className="min-w-[220px]">
          <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Client</span>
          <CautaAdaugaPartener
            baza="/sellout"
            toti={(clientsQ.data?.clients ?? []).map((c) => ({ id: c.id, nume: c.name }))}
            value={
              clientId != null
                ? {
                    id: clientId,
                    nume: clientsQ.data?.clients.find((c) => c.id === clientId)?.name ?? '…',
                  }
                : null
            }
            onChange={(p) => {
              if (p && !clientsQ.data?.clients.some((c) => c.id === p.id)) void clientsQ.refetch()
              setClientId(p?.id ?? null)
            }}
            placeholder="Caută client…"
          />
        </div>
        <Button
          variant="secondary"
          disabled={!clientId || !sender.includes('@')}
          loading={link.isPending}
          onClick={() => link.mutate()}
        >
          Leagă
        </Button>
      </div>
      {domain && (
        <button
          type="button"
          onClick={() => setSender(domain)}
          className="mt-1 text-[11px] text-b4l-blue hover:underline"
        >
          Folosește tot domeniul ({domain}) — prinde orice adresă de la acest client
        </button>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'validat' || status === 'publicat'
      ? 'bg-green-100 text-green-800'
      : status === 'in_mapare'
        ? 'bg-amber-100 text-amber-800'
        : status === 'respins' || status === 'anulat'
          ? 'bg-red-100 text-red-800'
          : 'bg-gray-100 text-gray-700'
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function fmtPeriod(start: string, end: string): string {
  const s = new Date(start)
  const e = new Date(end)
  const fmt = (d: Date) => `${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`
  return fmt(s) === fmt(e) ? fmt(s) : `${fmt(s)} – ${fmt(e)}`
}

// ============================================================================
// Raport sell-in vs sell-out
// ============================================================================

interface ReportCell {
  productId: number
  sku: string
  productName: string
  month: string
  sellIn: number
  sellOut: number
  /** Valoarea sell-in, fara TVA: suma bucati × pret net de pe facturile noastre. */
  valIn: number
}

interface LocationCell {
  location: string
  month: string
  productId: number
  sku: string
  productName: string
  quantity: number
}

/**
 * Sell-out pe puncte de lucru: locațiile pe rânduri, lunile pe coloane. Doar sell-out —
 * sell-in-ul nu există per magazin, clientul e cel facturat. Filtrul pe produs lucrează
 * pe datele deja aduse, deci schimbarea lui nu mai interoghează serverul.
 */
function LocationsView({ clientId }: { clientId: number | null }) {
  const [productId, setProductId] = useState<number | null>(null)
  // Cantitățile pot fi fracționare; însumate în JS scot zecimale fantomă.
  const fmt = (n: number) => Math.round(n * 1000) / 1000

  const q = useQuery<{ client: { name: string }; cells: LocationCell[] }>({
    queryKey: ['sellout', 'by-location', clientId],
    queryFn: async () => (await api.get('/sellout/by-location', { params: { clientId } })).data,
    enabled: !!clientId,
  })

  const all = q.data?.cells ?? []
  const productOptions = [
    ...new Map(
      all.map((c) => [c.productId, { value: c.productId, label: c.productName }]),
    ).values(),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const cells = productId == null ? all : all.filter((c) => c.productId === productId)
  const months = [...new Set(cells.map((c) => c.month))].sort()

  // locație → lună → cantitate, plus totalul pe locație (ordinea rândurilor).
  const byLocation = new Map<string, Map<string, number>>()
  const totalOf = new Map<string, number>()
  for (const c of cells) {
    let row = byLocation.get(c.location)
    if (!row) {
      row = new Map()
      byLocation.set(c.location, row)
    }
    row.set(c.month, (row.get(c.month) ?? 0) + c.quantity)
    totalOf.set(c.location, (totalOf.get(c.location) ?? 0) + c.quantity)
  }
  // Cele mai bune puncte de lucru primele — ordinea alfabetică n-ar spune nimic.
  const locations = [...byLocation.keys()].sort(
    (a, b) => (totalOf.get(b) ?? 0) - (totalOf.get(a) ?? 0) || a.localeCompare(b, 'ro'),
  )
  const monthTotal = (m: string) =>
    locations.reduce((s, l) => s + (byLocation.get(l)?.get(m) ?? 0), 0)
  const grandTotal = [...totalOf.values()].reduce((s, v) => s + v, 0)
  const onlyUnspecified = locations.length === 1 && locations[0] === 'Nespecificat'

  return (
    <div className="space-y-3">
      {q.isFetching && <span className="text-xs text-b4l-text-muted">Se calculează...</span>}

      {!clientId ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Defalcarea se face <strong>pentru un singur client</strong>. Alege-l din câmpul{' '}
          <strong>Client</strong>, sus în dreapta, ca să vezi cât s-a vândut în fiecare magazin sau
          farmacie, pe luni.
        </div>
      ) : locations.length === 0 ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Nu există sell-out validat pentru acest client.
        </div>
      ) : (
        <Card>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-b4l-text-muted">
              Cantități vândute (bucăți), pe punct de lucru și lună. Un singur import contează per
              lună — cel mai recent.
            </div>
            <label className="flex items-center gap-1.5 text-xs text-b4l-text-muted">
              Produs
              <div className="w-64">
                <SearchSelect
                  options={productOptions}
                  value={productId}
                  onChange={setProductId}
                  placeholder="toate produsele"
                />
              </div>
            </label>
          </div>

          {onlyUnspecified && (
            <div className="mb-2 rounded border border-b4l-amber-dark/40 bg-amber-50 p-2 text-xs text-b4l-amber-dark">
              Raportul acestui client nu vine defalcat pe puncte de lucru — are doar totaluri.
              Defalcarea apare aici doar dacă fișierul primit are magazinele pe coloane sau o
              coloană de locație.
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-b4l-border text-left text-b4l-text-muted">
                  <th className="p-1.5 font-medium">Punct de lucru</th>
                  {months.map((m) => (
                    <th key={m} className="p-1.5 text-right font-medium whitespace-nowrap">
                      {m}
                    </th>
                  ))}
                  <th className="p-1.5 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((l) => (
                  <tr key={l} className="border-b border-b4l-border last:border-0">
                    <td className="p-1.5">{l}</td>
                    {months.map((m) => {
                      const v = byLocation.get(l)?.get(m)
                      return (
                        <td key={m} className="p-1.5 text-right whitespace-nowrap">
                          {v == null ? <span className="text-b4l-text-muted">—</span> : fmt(v)}
                        </td>
                      )
                    })}
                    <td className="p-1.5 text-right font-medium">{fmt(totalOf.get(l) ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-b4l-border font-medium">
                  <td className="p-1.5">Total</td>
                  {months.map((m) => (
                    <td key={m} className="p-1.5 text-right whitespace-nowrap">
                      {fmt(monthTotal(m))}
                    </td>
                  ))}
                  <td className="p-1.5 text-right">{fmt(grandTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

/** Clientul vine din filtrul comun al paginii — un singur selector, nu două. */
function ReportView({
  clientId,
  slotFiltre,
}: {
  clientId: number | null
  /** Nodul din antetul fix unde se randeaza bara de filtre. */
  slotFiltre: HTMLDivElement | null
}) {
  const reportQ = useQuery<{ client: { name: string }; cells: ReportCell[] }>({
    queryKey: ['sellout', 'report', clientId],
    queryFn: async () => (await api.get('/sellout/report', { params: { clientId } })).data,
    enabled: !!clientId,
  })

  // Filtrele lucreaza in pagina, nu pe server: raportul are ~20 produse x ~20 luni, adica
  // sub 500 de celule. E deja tot incarcat, deci filtrarea e instantanee si nu costa un drum.
  const [fromMonth, setFromMonth] = useState('')
  const [toMonth, setToMonth] = useState('')
  const [productQuery, setProductQuery] = useState('')
  const [hideEmpty, setHideEmpty] = useState(true)
  /** Bucati sau lei. Aceeasi matrice, alta unitate (Alexandru, 06.10.2026). */
  const [unitate, setUnitate] = useState<'buc' | 'lei'>('buc')

  /*
    Inaltimea ramasa sub card, masurata din pozitia lui reala. La montare pagina e sus, deci
    `top` e chiar pozitia naturala; se reface la redimensionare si cand se schimba clientul,
    fiindca bara de filtre se poate rupe pe doua randuri.
  */
  const cardRef = useRef<HTMLElement>(null)
  const [inaltimeCard, setInaltimeCard] = useState<number | undefined>(undefined)

  const cells = useMemo(() => reportQ.data?.cells ?? [], [reportQ.data])
  const allMonths = useMemo(() => [...new Set(cells.map((c) => c.month))].sort(), [cells])

  /*
    Pretul cu care evaluam sell-out-ul in lei: media ponderata a facturilor NOASTRE catre client
    pentru acel produs (valoare sell-in / bucati sell-in), pe TOT istoricul — nu pe perioada
    filtrata, ca o schimbare de filtru sa nu schimbe pretul. Aceeasi regula ca acoperirea
    (`coverage.ts`): pretul de raft al clientului n-ar fi comparabil cu facturile noastre.
    Un produs pe care nu i l-am facturat niciodata n-are pret al nostru: nu se evalueaza.
  */
  const pretMediu = useMemo(() => {
    const sume = new Map<number, { val: number; qty: number }>()
    for (const c of cells) {
      const s = sume.get(c.productId) ?? { val: 0, qty: 0 }
      s.val += c.valIn
      s.qty += c.sellIn
      sume.set(c.productId, s)
    }
    const m = new Map<number, number>()
    for (const [pid, s] of sume) if (s.qty > 0) m.set(pid, s.val / s.qty)
    return m
  }, [cells])

  /** Sell-in in unitatea aleasa. */
  const vIn = (c: ReportCell | undefined): number =>
    !c ? 0 : unitate === 'lei' ? c.valIn : c.sellIn
  /** Sell-out in unitatea aleasa; `null` = in lei, dar produsul n-are pret al nostru. */
  const vOut = (c: ReportCell | undefined): number | null => {
    if (!c) return 0
    if (unitate === 'buc') return c.sellOut
    const p = pretMediu.get(c.productId)
    return p == null ? (c.sellOut ? null : 0) : c.sellOut * p
  }
  /** Afisarea unei cifre: bucatile ca atare, leii rotunjiti la leu. */
  const fmt = (v: number) =>
    unitate === 'lei' ? Math.round(v).toLocaleString('ro-RO') : String(Math.round(v * 1000) / 1000)

  // Anii din date, pentru butoanele rapide. Nu-i hardcodam: raportul creste in fiecare an.
  const years = useMemo(
    () => [...new Set(allMonths.map((m) => m.slice(0, 4)))].sort().reverse(),
    [allMonths],
  )

  // Presetari, ca la eMag. Matricea e pe LUNI, deci toate lucreaza in „AAAA-LL"; conversia
  // la zile se face doar la marginea cu selectorul de calendar.
  const presets = useMemo(() => {
    const now = new Date()
    const y = now.getFullYear()
    const mm = (n: number) => String(n).padStart(2, '0')
    const prev = new Date(y, now.getMonth() - 1, 1)
    const list: { key: string; label: string; from: string; to: string }[] = [
      { key: 'tot', label: 'Tot', from: '', to: '' },
      {
        key: 'lunaCurenta',
        label: 'Luna curentă',
        from: `${y}-${mm(now.getMonth() + 1)}`,
        to: `${y}-${mm(now.getMonth() + 1)}`,
      },
      {
        key: 'lunaPrecedenta',
        label: 'Luna precedentă',
        from: `${prev.getFullYear()}-${mm(prev.getMonth() + 1)}`,
        to: `${prev.getFullYear()}-${mm(prev.getMonth() + 1)}`,
      },
    ]
    for (const yr of years)
      list.push({ key: `an-${yr}`, label: yr, from: `${yr}-01`, to: `${yr}-12` })
    return list
  }, [years])

  // Trimestrele stau intr-o lista, nu ca butoane: cu doi ani de date ar fi opt butoane in
  // plus, iar bara ar deveni mai greu de citit decat tabelul pe care il filtreaza.
  const quarters = useMemo(
    () =>
      years.flatMap((yr) =>
        [1, 2, 3, 4].map((q) => ({
          key: `${yr}-Q${q}`,
          label: `Q${q} ${yr}`,
          from: `${yr}-${String(q * 3 - 2).padStart(2, '0')}`,
          to: `${yr}-${String(q * 3).padStart(2, '0')}`,
        })),
      ),
    [years],
  )

  const activeKey =
    presets.find((p) => p.from === fromMonth && p.to === toMonth)?.key ??
    quarters.find((p) => p.from === fromMonth && p.to === toMonth)?.key ??
    ''

  /** „AAAA-LL" -> prima/ultima zi, pentru selectorul de calendar (care lucreaza pe zile). */
  const monthToDay = (m: string, end: boolean) => {
    if (!m) return ''
    const [yy, mo] = m.split('-').map(Number)
    return end ? `${m}-${String(new Date(yy, mo, 0).getDate()).padStart(2, '0')}` : `${m}-01`
  }

  const months = useMemo(
    () => allMonths.filter((m) => (!fromMonth || m >= fromMonth) && (!toMonth || m <= toMonth)),
    [allMonths, fromMonth, toMonth],
  )

  // Grupare pe produs, cu celule per lună.
  const products = useMemo(() => {
    const byProduct = new Map<
      number,
      { sku: string; name: string; cells: Map<string, ReportCell> }
    >()
    for (const c of cells) {
      let p = byProduct.get(c.productId)
      if (!p) {
        p = { sku: c.sku, name: c.productName, cells: new Map() }
        byProduct.set(c.productId, p)
      }
      p.cells.set(c.month, c)
    }
    const q = productQuery.trim().toLowerCase()
    return (
      [...byProduct.values()]
        .filter((p) => !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
        // Un produs fara nicio cifra in perioada aleasa e un rand de liniute. Ascuns implicit:
        // altfel filtrarea pe un an ingusteaza coloanele, dar lasa tabelul la fel de lung.
        .filter(
          (p) =>
            !hideEmpty ||
            months.some((m) => (p.cells.get(m)?.sellIn ?? 0) || (p.cells.get(m)?.sellOut ?? 0)),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [cells, productQuery, hideEmpty, months])

  useLayoutEffect(() => {
    const masoara = () => {
      const el = cardRef.current
      if (!el) return
      // 16px marja jos, ca sa nu lipim cardul de marginea ferestrei.
      setInaltimeCard(Math.max(320, window.innerHeight - el.getBoundingClientRect().top - 16))
    }
    masoara()
    window.addEventListener('resize', masoara)
    return () => window.removeEventListener('resize', masoara)
  }, [clientId, cells.length])

  const filtered = months.length !== allMonths.length || productQuery.trim() !== '' || hideEmpty

  // Totalurile insumeaza EXACT ce se vede: produsele ramase dupa filtre, pe lunile ramase.
  // Daca ar aduna tot setul, ar contrazice tabelul de deasupra lor — iar cifra pe care omul
  // o citeste ultima e cea pe care o crede.
  const totals = useMemo(() => {
    const perMonth = new Map<string, { sellIn: number; sellOut: number }>()
    let sellIn = 0
    let sellOut = 0
    // In lei: produsele vandute de client, dar nefacturate de noi niciodata — fara pret, deci
    // in afara totalului. Se numara si se spun pe ecran, ca diferenta sa nu para mai mare.
    const faraPret = new Set<string>()
    for (const m of months) {
      let mi = 0
      let mo = 0
      for (const p of products) {
        const c = p.cells.get(m)
        mi += vIn(c)
        const o = vOut(c)
        if (o == null) faraPret.add(p.sku)
        else mo += o
      }
      perMonth.set(m, { sellIn: mi, sellOut: mo })
      sellIn += mi
      sellOut += mo
    }
    return { perMonth, sellIn, sellOut, faraPret: faraPret.size }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, months, unitate, pretMediu])

  return (
    <div className="space-y-3">
      {reportQ.isFetching && <span className="text-xs text-b4l-text-muted">Se calculează...</span>}

      {!clientId ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Raportul se face <strong>pentru un singur client</strong>. Alege-l din câmpul{' '}
          <strong>Client</strong>, sus în dreapta, ca să vezi sell-in (facturat de noi) vs. sell-out
          (vândut de el), pe produs și lună.
        </div>
      ) : cells.length === 0 ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Nu există date pentru acest client (nici facturi în EA, nici importuri sell-out
          publicate).
        </div>
      ) : (
        /*
          Cardul se opreste exact la marginea de jos a ecranului, deci pagina n-are ce derula:
          tot scroll-ul se intampla in tabel, iar antetul, filtrele si capul de coloane raman
          pe ecran (cerut de Alexandru, 16.09.2026).

          Inaltimea se MASOARA, nu se calculeaza din `--b4l-antet`. Doua incercari cu
          `calc(100vh - var(--b4l-antet) ...)` au dat gres, fiindca antetul e `sticky` fata de
          `<main>`, iar cardul fata de viewport — doua sisteme de coordonate diferite, deci
          scaderea nu cadea niciodata unde trebuie.
        */
        <Card ref={cardRef} className="flex flex-col" style={{ maxHeight: inaltimeCard }}>
          {/*
            Bara de filtre se randeaza in ANTETUL FIX, prin portal — starea ramane aici,
            fiindca presetarile depind de lunile din date. Vezi slotul din `SellOutPage`.
          */}
          {slotFiltre &&
            createPortal(
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {presets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => {
                      setFromMonth(p.from)
                      setToMonth(p.to)
                    }}
                    className={`rounded-md border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
                      activeKey === p.key
                        ? 'border-b4l-green bg-b4l-green text-white'
                        : 'border-b4l-border bg-white text-b4l-text hover:border-b4l-green hover:bg-b4l-green-light/30'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}

                <select
                  value={quarters.some((q) => q.key === activeKey) ? activeKey : ''}
                  onChange={(e) => {
                    const q = quarters.find((x) => x.key === e.target.value)
                    if (q) {
                      setFromMonth(q.from)
                      setToMonth(q.to)
                    }
                  }}
                  className={`rounded-md border px-2 py-1.5 text-xs shadow-sm ${
                    quarters.some((q) => q.key === activeKey)
                      ? 'border-b4l-green bg-b4l-green text-white'
                      : 'border-b4l-border bg-white'
                  }`}
                >
                  <option value="">Trimestru…</option>
                  {quarters.map((q) => (
                    <option key={q.key} value={q.key} className="text-b4l-text">
                      {q.label}
                    </option>
                  ))}
                </select>

                {/* Selectorul manual, acelasi ca la eMag. Lucreaza pe zile, matricea pe luni:
                convertim la margine, si rotunjim orice zi aleasa la luna ei intreaga —
                o matrice lunara n-are ce face cu „de la 17 martie". */}
                <div className="w-64">
                  <DateRangeField
                    fromValue={monthToDay(fromMonth, false)}
                    toValue={monthToDay(toMonth, true)}
                    onChange={(f, t) => {
                      setFromMonth(f ? f.slice(0, 7) : '')
                      setToMonth(t ? t.slice(0, 7) : '')
                    }}
                  />
                </div>

                {/* Bucati sau lei — aceeasi matrice, aceleasi filtre. */}
                <div className="flex overflow-hidden rounded-md border border-b4l-border shadow-sm">
                  {(
                    [
                      ['buc', 'Bucăți'],
                      ['lei', 'Lei'],
                    ] as const
                  ).map(([u, eticheta]) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setUnitate(u)}
                      className={`px-3 py-1.5 text-xs font-semibold ${
                        unitate === u
                          ? 'bg-b4l-green text-white'
                          : 'bg-white text-b4l-text hover:bg-b4l-green-light/30'
                      }`}
                    >
                      {eticheta}
                    </button>
                  ))}
                </div>

                <input
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Caută produs sau SKU…"
                  className="ml-auto w-52 rounded-md border border-gray-400 bg-white px-2 py-1 text-xs shadow-sm"
                />
                <label className="flex items-center gap-1.5 text-xs text-b4l-text-muted">
                  <input
                    type="checkbox"
                    checked={hideEmpty}
                    onChange={(e) => setHideEmpty(e.target.checked)}
                  />
                  Ascunde produsele fără date
                </label>
              </div>,
              slotFiltre,
            )}

          <div className="mb-2 shrink-0 text-xs text-b4l-text-muted">
            În fiecare celulă: <span className="font-medium text-b4l-blue">sell-in</span> /{' '}
            <span className="font-medium text-green-700">sell-out</span>. Sell-in = ce am facturat
            noi; sell-out = ce a vândut clientul.
            {unitate === 'lei' && (
              <>
                {' '}
                În lei, fără TVA: sell-in-ul e valoarea facturilor noastre, iar sell-out-ul e{' '}
                <b>{ETICHETE_CIFRE.sellOutLei.detaliu}</b> — prețul mediu cu care i-am facturat
                clientului fiecare produs.
              </>
            )}
            {filtered && (
              <>
                {' · '}
                <span className="font-medium">
                  {products.length} produse × {months.length} luni
                </span>{' '}
                (filtrat)
              </>
            )}
          </div>
          {products.length === 0 ? (
            <div className="rounded border border-dashed border-b4l-border p-6 text-center text-sm text-b4l-text-muted">
              Filtrele curente nu lasă niciun rând. Datele există — încearcă altă perioadă, alt text
              de căutare, sau debifează „Ascunde produsele fără date”.
            </div>
          ) : null}

          {/* Antetul si coloana Produs raman fixe la derulare: matricea are zeci de luni pe
              orizontala si zeci de produse pe verticala, iar fara ele nu mai stii ce citesti.
              Ambele au nevoie de fundal opac — altfel continutul derulat se vede prin ele. */}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-xs">
              <thead>
                <tr className="text-left text-b4l-text-muted">
                  <th className="sticky left-0 top-0 z-20 border-b border-b4l-border bg-white p-1.5 font-medium">
                    Produs
                  </th>
                  {months.map((m) => (
                    <th
                      key={m}
                      className="sticky top-0 z-10 border-b border-b4l-border bg-white p-1.5 text-right font-medium whitespace-nowrap"
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.sku}>
                    <td className="sticky left-0 z-10 border-b border-b4l-border bg-white p-1.5">
                      <div className="font-medium">{p.name}</div>
                      <div className="font-mono text-[10px] text-b4l-text-muted">{p.sku}</div>
                    </td>
                    {months.map((m) => {
                      const c = p.cells.get(m)
                      return (
                        <td
                          key={m}
                          className="border-b border-b4l-border p-1.5 text-right whitespace-nowrap"
                        >
                          {c ? (
                            <>
                              <span className="text-b4l-blue">{fmt(vIn(c))}</span>
                              <span className="text-b4l-text-muted"> / </span>
                              {(() => {
                                const o = vOut(c)
                                return o == null ? (
                                  <span
                                    className="text-amber-700"
                                    title="Produs nefacturat de noi clientului — fără preț al nostru"
                                  >
                                    fără preț
                                  </span>
                                ) : (
                                  <span className="text-green-700">{fmt(o)}</span>
                                )
                              })()}
                            </>
                          ) : (
                            <span className="text-b4l-text-muted">—</span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>

              {/* Totalurile se lipesc de marginea de jos a CONTAINERULUI, nu a ferestrei.
                  Cand tabelul e mai scurt decat containerul, acesta se strange pe continut
                  si randul sta firesc dupa ultimul produs — nu pluteste peste spatiu gol.
                  Merita fixat mai ales din cauza derularii pe orizontala: te uiti la o luna
                  din mijloc si vrei totalul ei sub ochi. */}
              {products.length > 0 && (
                <tfoot>
                  <tr className="font-semibold">
                    <td className="sticky bottom-0 left-0 z-20 border-t-2 border-b4l-border bg-b4l-bg-soft p-1.5">
                      Total
                      <div className="text-[10px] font-normal text-b4l-text-muted">
                        {products.length} produse × {months.length} luni
                      </div>
                    </td>
                    {months.map((m) => {
                      const t = totals.perMonth.get(m)
                      return (
                        <td
                          key={m}
                          className="sticky bottom-0 z-10 border-t-2 border-b4l-border bg-b4l-bg-soft p-1.5 text-right whitespace-nowrap"
                        >
                          <span className="text-b4l-blue">{fmt(t?.sellIn ?? 0)}</span>
                          <span className="text-b4l-text-muted"> / </span>
                          <span className="text-green-700">{fmt(t?.sellOut ?? 0)}</span>
                        </td>
                      )
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
            <span className="text-b4l-text-muted">Total pe tot ce e afișat:</span>
            <span>
              sell-in <b className="text-b4l-blue">{fmt(totals.sellIn)}</b>
            </span>
            <span>
              sell-out <b className="text-green-700">{fmt(totals.sellOut)}</b>
              {unitate === 'lei' && (
                <span className="text-xs text-b4l-text-muted">
                  {' '}
                  ({ETICHETE_CIFRE.sellOutLei.detaliu})
                </span>
              )}
            </span>
            {/* Diferenta e citirea utila a raportului: cat am livrat si inca nu s-a vandut. In
                lei e chiar valoarea estimata a marfii noastre ramase la client. */}
            <span className="text-b4l-text-muted">
              diferență {fmt(totals.sellIn - totals.sellOut)}
              {unitate === 'lei' ? ' lei — stoc estimat la client' : ''}
            </span>
            {unitate === 'lei' && totals.faraPret > 0 && (
              <span className="text-xs text-amber-700">
                {totals.faraPret} {totals.faraPret === 1 ? 'produs' : 'produse'} fără preț al nostru
                (vândute, dar nefacturate de noi clientului) — nu intră în totalul sell-out.
              </span>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}

// ============================================================================
// Import modal: client + fișier → preview → confirm
// ============================================================================

function ImportModal({
  onClose,
  fromEmail,
  agent,
  canFilter,
}: {
  onClose: () => void
  fromEmail?: EmailCandidate
  agent: string
  canFilter: boolean
}) {
  const queryClient = useQueryClient()
  const [clientId, setClientId] = useState<number | null>(fromEmail?.guessedClientId ?? null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  // Luna raportului. Multe fișiere n-o conțin deloc (Farmacia Tei: doar produs, cantitate,
  // gestiune), iar din numele „dec" anul se ghicea — și se ghicea diferit de la un lot la
  // altul. Aici operatorul o poate impune.
  const [month, setMonth] = useState<string>('')
  // Analiza fișierului la atașare (fără AI): de unde se poate ști perioada.
  const [src, setSrc] = useState<MonthSource | null>(null)
  // Foile alese pentru import. Un registru cu mai multe foi de date nu se mai alege singur:
  // lua foaia cea mai mare, care la Dr Max era istoricul de promoții, nu raportul.
  const [sheets, setSheets] = useState<string[]>([])
  // Mapări alese de operator pentru denumirile nerecunoscute: denumire client → productId.
  const [mappings, setMappings] = useState<Record<string, number>>({})

  const clientsQ = useQuery<{ clients: { id: number; name: string }[] }>({
    queryKey: ['sellout', 'clients', agent],
    queryFn: async () =>
      (await api.get('/sellout/clients', { params: agentParams(agent, canFilter) })).data,
  })

  const productsQ = useQuery<{ products: { id: number; sku: string; name: string }[] }>({
    queryKey: ['sellout', 'products'],
    queryFn: async () => (await api.get('/sellout/products')).data,
  })
  const productOptions = (productsQ.data?.products ?? []).map((p) => ({
    value: p.id,
    label: `${p.name} (${p.sku})`,
  }))

  // Analiza fișierului la atașare — citirea grilei, fără AI, deci răspunde imediat.
  // Rostul ei: dacă perioada nu se poate ști (fișierul n-are date, iar numele n-are an),
  // operatorul e întrebat ÎNAINTE de extragere, nu descoperă anul greșit după import.
  const inspectMut = useMutation({
    mutationFn: async () => {
      if (fromEmail) {
        return (
          await api.post<MonthSource>('/sellout/inspect-from-email', {
            messageId: fromEmail.messageId,
            attachmentIdx: fromEmail.attachmentIdx,
          })
        ).data
      }
      const fd = new FormData()
      fd.append('file', file as File)
      return (await api.post<MonthSource>('/sellout/inspect', fd)).data
    },
    onSuccess: (data) => {
      setSrc(data)
      // Propunem luna dedusă; operatorul o poate schimba.
      if (data.suggestedMonth) setMonth(data.suggestedMonth)
      // O singură foaie cu date ⇒ bifată. Mai multe ⇒ niciuna, alege operatorul.
      setSheets(data.suggestedSheets ?? [])
    },
    onError: showError,
  })

  // Se declanșează la deschiderea modalului (din email) și la alegerea fișierului.
  useEffect(() => {
    if (file || fromEmail) inspectMut.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, fromEmail?.messageId, fromEmail?.attachmentIdx])

  const previewMut = useMutation({
    mutationFn: async () => {
      // Extragerea AI merge pe loturi de rânduri: un raport mare depășește lejer
      // timeout-ul implicit de 15s al clientului. Îl ridicăm doar aici.
      const opts = { timeout: 300_000 }
      // Din email: fișierul e deja în CRM (atașament salvat) — nu se încarcă nimic.
      if (fromEmail) {
        return (
          await api.post<PreviewResult>(
            '/sellout/preview-from-email',
            {
              messageId: fromEmail.messageId,
              attachmentIdx: fromEmail.attachmentIdx,
              clientId,
              month: month || null,
              sheets: sheets.length > 0 ? sheets : undefined,
            },
            opts,
          )
        ).data
      }
      const fd = new FormData()
      fd.append('file', file as File)
      if (clientId) fd.append('clientId', String(clientId))
      if (month) fd.append('month', month)
      if (sheets.length > 0) fd.append('sheets', JSON.stringify(sheets))
      return (await api.post<PreviewResult>('/sellout/preview', fd, opts)).data
    },
    onSuccess: (data) => {
      setPreview(data)
      // Pre-selectam propunerile: altfel produsul aparea ales in lista, dar la salvare
      // nu se trimitea nimic (se trimit doar `mappings`), iar randul ramanea nemapat.
      const seeded: Record<string, number> = {}
      for (const r of data.rows) {
        if (r.productId == null && r.productName && r.suggestion) {
          seeded[r.productName] = r.suggestion.id
        }
      }
      setMappings(seeded)
    },
    onError: showError,
  })

  const confirmMut = useMutation({
    mutationFn: async () =>
      (
        await api.post(
          '/sellout/confirm',
          {
            clientId,
            fileName: preview!.fileName,
            contentHash: preview!.contentHash,
            rows: preview!.rows,
            mappings: Object.entries(mappings).map(([clientProductName, productId]) => ({
              clientProductName,
              productId,
            })),
          },
          // Sute de rânduri + mapările lor: și salvarea poate depăși 15s.
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sellout', 'imports'] })
      toast.success('Import salvat')
      onClose()
    },
    onError: showError,
  })

  // Denumirile distincte nerecunoscute (fără produs) — de mapat manual.
  const unmatchedNames = preview
    ? [
        ...new Set(
          preview.rows
            .filter((r) => r.productId == null && r.productName)
            .map((r) => r.productName as string),
        ),
      ]
    : []

  // Sugestia serverului pentru fiecare denumire nerecunoscută (potrivire pe cuvinte).
  const suggestionByName = new Map<
    string,
    { id: number; name: string; sku: string; score: number }
  >()
  const tiedByName = new Map<string, string[]>()
  for (const r of preview?.rows ?? []) {
    if (r.productId != null || !r.productName) continue
    if (r.suggestion) suggestionByName.set(r.productName, r.suggestion)
    else if (r.suggestionTiedSkus?.length) tiedByName.set(r.productName, r.suggestionTiedSkus)
  }

  // Fara luna nu se extrage, cand nici fisierul si nici numele lui nu o spun: altfel
  // sistemul ar ghici anul, exact greseala care a dus rapoartele Farmacia Tei in 2024.
  const lunaLipsa = !!src?.needsOperator && !month
  // Foile cu date din registru. Când sunt mai multe, nici una nu vine bifată: alegerea
  // automată e fix ce ne-a dat rânduri din foaia greșită.
  const foiCuDate = (src?.sheets ?? []).filter((s) => s.hasData)
  const foiLipsa = foiCuDate.length > 1 && sheets.length === 0
  const canPreview =
    (!!file || !!fromEmail) && !!clientId && !lunaLipsa && !foiLipsa && !inspectMut.isPending
  const canConfirm = !!preview && !!clientId && preview.rows.length > 0

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Import sell-out"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Închide
          </Button>
          {!preview ? (
            <Button
              onClick={() => previewMut.mutate()}
              loading={previewMut.isPending}
              disabled={!canPreview}
            >
              Extrage și previzualizează
            </Button>
          ) : (
            <Button
              onClick={() => confirmMut.mutate()}
              loading={confirmMut.isPending}
              disabled={!canConfirm}
            >
              Confirmă și salvează
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Client</label>
            <CautaAdaugaPartener
              baza="/sellout"
              toti={(clientsQ.data?.clients ?? []).map((c) => ({ id: c.id, nume: c.name }))}
              value={
                clientId != null
                  ? {
                      id: clientId,
                      nume: clientsQ.data?.clients.find((c) => c.id === clientId)?.name ?? '…',
                    }
                  : null
              }
              onChange={(p) => {
                if (p && !clientsQ.data?.clients.some((c) => c.id === p.id)) void clientsQ.refetch()
                setClientId(p?.id ?? null)
              }}
              placeholder="Alege clientul..."
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              {fromEmail ? 'Fișier din email' : 'Fișier (XLSX / XLS / ODS / CSV)'}
            </label>
            {fromEmail ? (
              <div className="rounded border border-b4l-border bg-b4l-bg px-2.5 py-1.5 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate font-medium" title={fromEmail.fileName}>
                    📎 {fromEmail.fileName}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-b4l-blue hover:underline"
                    onClick={() =>
                      downloadFile(
                        `/sellout/email-attachment/${fromEmail.messageId}/${fromEmail.attachmentIdx}`,
                        {},
                        fromEmail.fileName,
                      ).catch((e: unknown) => toast.error((e as Error).message))
                    }
                  >
                    descarcă
                  </button>
                </div>
                <div className="truncate text-b4l-text-muted" title={fromEmail.subject}>
                  {fromEmail.subject}
                </div>
              </div>
            ) : (
              <input
                type="file"
                accept=".xlsx,.xls,.ods,.csv"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null)
                  setPreview(null)
                }}
                className="block w-full text-sm"
              />
            )}
          </div>
        </div>

        {inspectMut.isPending && (
          <div className="text-xs text-b4l-text-muted">Se citește fișierul…</div>
        )}

        {foiCuDate.length > 1 && (
          <div
            className={`rounded border p-3 ${
              foiLipsa ? 'border-b4l-amber-dark/40 bg-amber-50' : 'border-b4l-border bg-b4l-bg'
            }`}
          >
            <div className="mb-1 text-xs font-medium text-b4l-text-muted">
              Fișierul are {foiCuDate.length} foi cu date
              {foiLipsa ? (
                <span className="ml-1 text-b4l-amber-dark">— bifează ce se importă</span>
              ) : (
                <span className="font-normal"> — se importă cele bifate</span>
              )}
            </div>
            <div className="space-y-1.5">
              {foiCuDate.map((s) => (
                <label key={s.name} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={sheets.includes(s.name)}
                    onChange={(e) => {
                      setSheets((prev) =>
                        e.target.checked ? [...prev, s.name] : prev.filter((n) => n !== s.name),
                      )
                      setPreview(null)
                    }}
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{s.name}</span>
                    <span className="ml-1.5 text-[11px] text-b4l-text-muted">{s.rows} rânduri</span>
                    {s.hidden && (
                      <span className="ml-1.5 rounded bg-b4l-bg px-1 py-0.5 text-[10px] text-b4l-text-muted">
                        ascunsă în Excel
                      </span>
                    )}
                    {s.preview.length > 0 && (
                      <span className="mt-0.5 block truncate text-[11px] text-b4l-text-muted">
                        {s.preview[0].replace(/\t/g, ' · ')}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {foiLipsa && (
              <div className="mt-1.5 text-[11px] text-b4l-amber-dark">
                Nu aleg eu: foaia cea mai mare e adesea un istoric sau un pivot, nu raportul.
              </div>
            )}
          </div>
        )}

        {src && (
          <div
            className={`rounded border p-3 ${
              lunaLipsa ? 'border-b4l-amber-dark/40 bg-amber-50' : 'border-b4l-border bg-b4l-bg'
            }`}
          >
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Luna raportului
              {src.needsOperator ? (
                <span className="ml-1 text-b4l-amber-dark">— alege-o, fișierul nu o spune</span>
              ) : (
                <span className="font-normal"> (o poți schimba)</span>
              )}
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="month"
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value)
                  setPreview(null)
                }}
                className={`rounded border px-2 py-1 text-sm ${
                  lunaLipsa ? 'border-b4l-amber-dark' : 'border-b4l-border'
                }`}
              />
              <span className="text-[11px] text-b4l-text-muted">
                {src.certainty === 'grila'
                  ? `Fișierul conține datele (${src.gridLines} rânduri citite) — luna se ia din el.`
                  : src.certainty === 'nume-cu-an'
                    ? `Dedusă din numele fișierului, care conține și anul.`
                    : src.certainty === 'nume-fara-an'
                      ? `Numele fișierului dă luna, dar nu și anul. Am propus ultima lună încheiată cu acest nume — confirmă sau schimbă anul.`
                      : `Nici fișierul, nici numele lui nu spun perioada.`}
              </span>
            </div>
            {lunaLipsa && (
              <div className="mt-1.5 text-[11px] text-b4l-amber-dark">
                Fără lună nu pot extrage: anul ar fi ghicit, iar rândurile ar ajunge pe o perioadă
                greșită.
              </div>
            )}
          </div>
        )}

        {preview && (
          <>
            {preview.aiUnavailable && (
              <div className="rounded border border-b4l-amber-dark/40 bg-amber-50 p-3 text-xs text-b4l-amber-dark">
                <div className="font-medium">Extragerea automată nu a putut rula.</div>
                <div className="mt-0.5">{preview.aiError}</div>
                <div className="mt-1 text-b4l-text-muted">
                  Fișierul a fost citit (vezi structura mai jos), dar rândurile nu au putut fi
                  extrase. Verifică creditele API în Setări → AI, apoi reîncearcă.
                </div>
                {preview.gridPreview.length > 0 && (
                  <pre className="mt-2 max-h-48 overflow-auto rounded bg-white/60 p-2 text-[11px]">
                    {preview.gridPreview.join('\n')}
                  </pre>
                )}
              </div>
            )}

            {!preview.aiUnavailable && preview.rows.length === 0 && (
              <div className="rounded border border-b4l-red/40 bg-b4l-red-light p-3 text-xs text-b4l-red-dark">
                <div className="font-medium">
                  Fișierul a fost citit, dar nu s-a extras niciun rând.
                </div>
                {preview.notes && (
                  <div className="mt-0.5">Ce a raportat extragerea: {preview.notes}</div>
                )}
                <div className="mt-1 text-b4l-text-muted">
                  Structura citită e mai jos. Dacă rândurile de date se văd aici, extragerea a eșuat
                  (nu fișierul) — trimite structura la dezvoltare.
                </div>
                {preview.gridPreview.length > 0 && (
                  <pre className="mt-2 max-h-48 overflow-auto rounded bg-white/60 p-2 text-[11px] text-b4l-text">
                    {preview.gridPreview.join('\n')}
                  </pre>
                )}
              </div>
            )}

            {preview.overlaps?.length > 0 && (
              <div className="rounded border border-b4l-border bg-b4l-bg p-3">
                <div className="text-xs font-medium">
                  Clientul are deja {preview.overlaps.length} import
                  {preview.overlaps.length > 1 ? 'uri' : ''} pe aceleași luni. Fiind mai nou,
                  raportul de acum <strong>preia</strong> lunile comune — cantitățile nu se adună.
                </div>
                <ul className="mt-1.5 space-y-1">
                  {preview.overlaps.map((o) => (
                    <li key={o.id} className="text-[11px] text-b4l-text">
                      <span className="font-medium">{o.fileName}</span>{' '}
                      <span className="text-b4l-text-muted">
                        ({fmtPeriod(o.periodStart, o.periodEnd)}, {o.rowCount} rânduri)
                      </span>
                      {o.sharedMonths.length > 0 && (
                        <span className="text-b4l-text-muted">
                          {' '}
                          · comun: {o.sharedMonths.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-1.5 text-[11px] text-b4l-text-muted">
                  Importurile vechi rămân ca istoric; în listă apar marcate „înlocuit”. Verifică
                  doar dacă fișierul de acum e o bucată din lună (ex. o singură farmacie) — atunci
                  ar lua locul raportului complet.
                </div>
              </div>
            )}

            {unmatchedNames.length > 0 && (
              <div className="rounded border border-b4l-amber-dark/40 bg-amber-50 p-3">
                <div className="mb-2 text-xs font-medium text-b4l-amber-dark">
                  {unmatchedNames.length} denumiri fără cod (EAN/SKU) în raport — produsul a fost
                  propus după denumire. Verifică-l și, dacă e greșit, alege altul. Ce confirmi aici
                  se salvează în dicționarul clientului și se aplică automat data viitoare.
                </div>
                <div className="space-y-2">
                  {unmatchedNames.map((name) => {
                    const sug = suggestionByName.get(name)
                    // Sigur = potrivire completă a cuvintelor din denumirea clientului.
                    const sigur = !!sug && sug.score >= 0.99
                    const value = mappings[name] ?? null
                    return (
                      <div key={name} className="grid grid-cols-2 items-center gap-2">
                        <span className="truncate text-xs" title={name}>
                          {name}
                        </span>
                        <div>
                          <SearchSelect
                            options={productOptions}
                            value={value}
                            onChange={(v) =>
                              setMappings((m) => {
                                const next = { ...m }
                                if (v == null) delete next[name]
                                else next[name] = v
                                return next
                              })
                            }
                            placeholder="Alege produsul..."
                          />
                          {sug && mappings[name] === sug.id && (
                            <div
                              className={`mt-0.5 text-[10px] ${sigur ? 'text-green-700' : 'text-b4l-amber-dark'}`}
                            >
                              {sigur
                                ? `potrivire completă a denumirii · ${sug.sku}`
                                : `potrivire parțială (${Math.round(sug.score * 100)}%) — verifică · ${sug.sku}`}
                            </div>
                          )}
                          {!sug && tiedByName.get(name) && (
                            <div className="mt-0.5 text-[10px] text-b4l-amber-dark">
                              {tiedByName.get(name)!.length} produse cu aceeași denumire (
                              {tiedByName.get(name)!.join(', ')}) — alege tu
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {preview.rows.length > 0 && (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-b4l-text-muted">
                    {preview.rows.length} rânduri extrase
                    {preview.unmatchedCount > 0 && (
                      <span className="ml-2 text-b4l-amber-dark">
                        · {preview.unmatchedCount} produse nerecunoscute
                      </span>
                    )}
                  </span>
                  {preview.notes && <span className="text-b4l-text-muted">{preview.notes}</span>}
                </div>
                <div className="max-h-72 overflow-auto rounded border border-b4l-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-b4l-bg">
                      <tr className="text-left text-b4l-text-muted">
                        <th className="p-1.5">Cod</th>
                        <th className="p-1.5">Produs (client)</th>
                        <th className="p-1.5">SKU</th>
                        <th className="p-1.5">Produs intern</th>
                        <th className="p-1.5">Lună</th>
                        <th className="p-1.5 text-right">Cant.</th>
                        <th className="p-1.5">Locație</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => {
                        // Produsul ales pentru randurile fara cod: propunerea pre-selectata
                        // sau alegerea operatorului din caseta de sus. Randul arata ca unul
                        // identificat, dar marcat „dupa denumire".
                        const chosenId = r.productName ? mappings[r.productName] : undefined
                        const ales =
                          r.productId == null && chosenId != null
                            ? {
                                name:
                                  chosenId === r.suggestion?.id
                                    ? r.suggestion.name
                                    : (productOptions.find((o) => o.value === chosenId)?.label ??
                                      '—'),
                                sku: chosenId === r.suggestion?.id ? r.suggestion.sku : null,
                                sigur: chosenId === r.suggestion?.id && r.suggestion.score >= 0.99,
                              }
                            : null
                        return (
                          <tr
                            key={i}
                            className={`border-t border-b4l-border ${r.productId == null && !ales ? 'bg-amber-50' : ''}`}
                          >
                            <td className="p-1.5 font-mono">{r.ean ?? r.sku ?? '—'}</td>
                            <td
                              className="p-1.5 max-w-[200px] truncate"
                              title={r.productName ?? ''}
                            >
                              {r.productName ?? '—'}
                            </td>
                            <td className="p-1.5 font-mono whitespace-nowrap">
                              {r.sku_internal ?? ales?.sku ?? (
                                <span className="text-b4l-text-muted">—</span>
                              )}
                            </td>
                            <td className="p-1.5">
                              {r.productId != null ? (
                                <span className="max-w-[180px] truncate">
                                  {r.productName_internal}
                                </span>
                              ) : ales ? (
                                <span
                                  className={ales.sigur ? 'text-green-700' : 'text-b4l-amber-dark'}
                                  title={
                                    ales.sigur
                                      ? 'Ales după denumire — potrivire completă'
                                      : 'Ales după denumire — verifică sus'
                                  }
                                >
                                  {ales.name}
                                  <span className="ml-1 text-[10px] text-b4l-text-muted">
                                    (după denumire)
                                  </span>
                                </span>
                              ) : (
                                <span className="text-b4l-amber-dark">nerecunoscut</span>
                              )}
                            </td>
                            <td className="p-1.5">{r.month ?? '—'}</td>
                            <td className="p-1.5 text-right">{r.quantity}</td>
                            <td className="p-1.5 max-w-[120px] truncate">{r.location ?? '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {preview.unmatchedCount > 0 && (
                  <p className="mt-1 text-[11px] text-b4l-amber-dark">
                    Rândurile pe fond galben n-au produs ales — importul rămâne „În mapare” până le
                    rezolvi. Cele marcate „după denumire” au produsul propus prin potrivirea
                    denumirii; se confirmă odată cu salvarea.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
